import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { gql, useQuery } from '@apollo/client';
import { ShieldX } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import COVCertificateVerification from './components/COVCertificateVerification';
import CSAWCertificateVerification from './components/CSAWCertificateVerification';
import PLTCPCertificateVerification from './components/PLTCPCertificateVerification';
import PLTPCertificateVerification from './components/PLTPCertificateVerification';
import PTPRCertificateVerification from './components/PTPRCertificateVerification';
import SpecialTreeCuttingPermitVerification from './components/SpecialTreeCuttingPermitVerification';
import TCEBPCertificateVerification from './components/TCEBPCertificateVerification';

const GET_CERTIFICATE_VERIFICATION = gql`
   query GetCertificateVerification($id: ID!) {
      getCertificateById(id: $id) {
         id
         certificateNumber
         applicationType
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
      }
   }
`;

const VerifyCertificatePage = () => {
   const { id } = useParams();

   // Parse the ID parameter which might be a JSON string from a QR code
   const extractCertificateId = (idParam) => {
      if (!idParam) return null;

      try {
         // First, try to properly parse as JSON
         let parsed;
         try {
            parsed = JSON.parse(idParam);

            // If it contains a complete verification URL, extract and use that URL directly
            // This is the preferred way of handling the QR code scan
            if (parsed.verificationUrl) {
               // If on Android, we need to handle the URL differently
               if (navigator.userAgent.toLowerCase().indexOf("android") > -1) {
                  // Extract the ID from the end of the URL and redirect to the verification page
                  const urlParts = parsed.verificationUrl.split('/');
                  const id = urlParts[urlParts.length - 1];

                  // Redirect to the verification page with the extracted ID
                  window.location.href = parsed.verificationUrl;
                  return id;
               }

               // On iOS, this natural redirection happens automatically
               const urlParts = parsed.verificationUrl.split('/');
               return urlParts[urlParts.length - 1];
            }

            // If no URL found but has an id property
            if (parsed.id) {
               return parsed.id;
            }
         } catch (jsonError) {
            console.error('JSON parsing error:', jsonError);
            // Not valid JSON, continue to other extraction methods
         }

         // Special handling for malformed JSON from QR codes
         if (typeof idParam === 'string' && idParam.includes('verificationUrl')) {
            // Try to extract the URL using regex
            const urlMatch = idParam.match(/verificationUrl["\s:]+([^,"]+)/);
            if (urlMatch && urlMatch[1]) {
               const url = urlMatch[1].replace(/^"|"$/g, ''); // Remove surrounding quotes

               // For Android, force navigation to the URL
               if (navigator.userAgent.toLowerCase().indexOf("android") > -1) {
                  window.location.href = url;
               }

               // Extract the ID from the end of the URL
               const urlParts = url.split('/');
               return urlParts[urlParts.length - 1];
            }
         }

         // Check if it's a direct ObjectId (24 hex chars)
         if (/^[0-9a-f]{24}$/.test(idParam)) {
            return idParam;
         }

         // As a last resort, try to extract any 24-character hex string that looks like an ObjectId
         const objectIdMatch = idParam.match(/([0-9a-f]{24})/);
         if (objectIdMatch && objectIdMatch[1]) {
            return objectIdMatch[1];
         }

         // Fallback to the original param if no relevant fields found
         return idParam;
      } catch (e) {
         console.error('Error extracting certificate ID:', e);
         // If not valid JSON, use as-is (might be a direct ID)
         return idParam;
      }
   };

   const certificateId = extractCertificateId(id);

   useEffect(() => {
      console.log("Original QR data:", id);
      console.log("Extracted certificate ID:", certificateId);
   }, [id, certificateId]);

   const { loading, error, data } = useQuery(GET_CERTIFICATE_VERIFICATION, {
      variables: { id: certificateId },
      skip: !certificateId
   });

   if (!certificateId) {
      return (
         <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <Card className="max-w-md w-full p-6 text-center">
               <ShieldX className="mx-auto h-12 w-12 text-red-500 mb-4" />
               <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Certificate ID</h2>
               <p className="text-gray-600">
                  The certificate ID is missing or invalid. Please scan a valid QR code.
               </p>
            </Card>
         </div>
      );
   }

   if (loading) {
      return (
         <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
               <Skeleton className="h-8 w-full mb-4" />
               <Skeleton className="h-64 w-full" />
            </div>
         </div>
      );
   }

   if (error) {
      return (
         <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <Card className="max-w-md w-full p-6 text-center">
               <ShieldX className="mx-auto h-12 w-12 text-red-500 mb-4" />
               <h2 className="text-2xl font-bold text-gray-900 mb-2">Certificate Not Found</h2>
               <p className="text-gray-600">
                  We couldn't verify this certificate. It may be invalid or no longer exist.
               </p>
               <p className="text-sm text-gray-500 mt-2">Error details: {error.message}</p>
            </Card>
         </div>
      );
   }

   const certificate = data.getCertificateById;

   const renderCertificateVerification = () => {
      console.log('Certificate type:', certificate.applicationType);
      switch (certificate.applicationType) {
         case 'Chainsaw Registration':
            return <CSAWCertificateVerification certificate={certificate} />;
         case 'Certificate of Verification':
            return <COVCertificateVerification certificate={certificate} />;
         case 'Private Land Timber Permit':
            return <PLTPCertificateVerification certificate={certificate} />;
         case 'Private Land Timber Cutting Permit':
            return <PLTCPCertificateVerification certificate={certificate} />;
         case 'Public Land Tree Cutting Permit':
            return <PLTCPCertificateVerification certificate={certificate} />;
         case 'Private Tree Plantation Registration':
            return <PTPRCertificateVerification certificate={certificate} />;
         case 'Tree Cutting and Earth-Balling Permit':
            return <TCEBPCertificateVerification certificate={certificate} />;
         case 'Special Tree Cutting Permit':
            return <SpecialTreeCuttingPermitVerification certificate={certificate} />;
         default:
            return (
               <Card className="p-6 mb-6 text-center">
                  <ShieldX className="mx-auto h-12 w-12 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unsupported Certificate Type</h3>
                  <p className="text-gray-600">
                     This certificate type ({certificate.applicationType}) is not yet supported for verification.
                  </p>
                  <p className="text-gray-600 mt-2">
                     Certificate Number: {certificate.certificateNumber}
                  </p>
               </Card>
            );
      }
   };

   return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
         <div className="max-w-3xl mx-auto">
            {renderCertificateVerification()}
            <div className="text-center text-sm text-gray-500">
               <p>This is an official certificate verification page of DENR-PENRO Marinduque.</p>
               <p>For any concerns, please contact our office.</p>
            </div>
         </div>
      </div>
   );
};

export default VerifyCertificatePage;
